import React from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import css from 'styled-jsx/css';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

import 'react-datepicker/dist/react-datepicker.css';

import { ASSET_SIGNAL_CSV } from '../../Constants/TrackerClasses';

const { className: popperClass, styles: popperStyles } = css.resolve`
  @media (max-width: 767px) {
    .react-datepicker-popper {
      transform: none !important;
      left: calc(50% - 150px) !important;
      top: 20% !important;
      margin: 0 auto;
    }
  }
`;

export default function ExportSignalCollectionModalDisplay(props) {
  const {
    isOpen,
    toggleModal,
    startDate,
    endDate,
    requestPending,
    requestSuccessful,
    handleChangeStart,
    handleChangeEnd,
    handleSave,
    showTooManyJobsError,
    error,
    widget: { settings: { title = '' } = {} } = {},
    width,
  } = props;
  const desktopView = width >= 992;
  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg" centered>
      <ModalHeader toggle={toggleModal} className="text-nowrap">
        Export Data{' '}
        <span className="three-quarter-text text-text-lighter font-weight-normal">
          {title}
        </span>
      </ModalHeader>
      <ModalBody>
        {requestSuccessful ? (
          <div className="px-5 d-flex flex-column align-items-center">
            <div className="form-group w-100 mt-5 d-flex justify-content-center">
              <FontAwesomeIcon
                className="text-alert-confirmation"
                icon={faCheckCircle}
                size="5x"
              />
            </div>
            <div className="form-group w-100 mt-4 text-text-lighter text-center">
              {`Job successfully created. This process could take a few minutes.`}
              <br />
              {`We'll send a download link to your email when the process is
              complete.`}
            </div>
          </div>
        ) : (
          <div className="px-5 d-flex flex-column align-items-center">
            <div className="form-group w-100 mt-4 d-flex flex-column flex-md-row justify-content-center">
              <div className="d-flex flex-column">
                <label htmlFor="startDate">Start Date</label>
                <DatePicker
                  autoComplete="off"
                  className="form-control"
                  timeFormat="HH:mm"
                  dateFormat={[
                    'MMM d, yyyy H:mm',
                    'P',
                    'PP',
                    'PPP',
                    'MMM dd yyyy',
                    'MMMM dd yyyy',
                    'MM/yyyy',
                  ]}
                  maxDate={new Date()}
                  name="startDate"
                  onChange={handleChangeStart}
                  placeholderText="From"
                  popperClassName={popperClass}
                  selected={startDate}
                  selectsStart
                  showTimeSelect
                  timeCaption="time"
                  timeIntervals={15}
                />
              </div>
              <div className="align-self-end mx-4 mb-2 d-none d-md-block">
                &#8212;
              </div>
              <hr className="d-block d-md-none" />
              <div className="d-flex flex-column">
                <label htmlFor="startDate">End Date</label>
                <DatePicker
                  autoComplete="off"
                  className="form-control"
                  dateFormat={[
                    'MMM d, yyyy H:mm',
                    'P',
                    'PP',
                    'PPP',
                    'MMM dd yyyy',
                    'MMMM dd yyyy',
                    'MM/yyyy',
                  ]}
                  maxDate={new Date()}
                  minDate={startDate}
                  name="endDate"
                  onChange={handleChangeEnd}
                  placeholderText="To"
                  popperClassName={popperClass}
                  selected={endDate}
                  selectsEnd
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="time"
                />
              </div>
            </div>
            <div
              className={`${
                desktopView && 'w-75 px-3 mt-4'
              } px-0 mt-2 form-group text-muted three-quarter-text`}
            >
              {`Maximum of 60,000 datapoints per signal collection. All the
                signals in the signal collection(s) in this smart tile will be
                exported.`}
            </div>
            <div
              className={`${!showTooManyJobsError && 'd-none'} ${
                desktopView && 'w-75 px-3 mt-4'
              } form-group text-danger three-quarter-text`}
            >
              {`You already have 5 jobs in progress. Keep an eye on your email and
              check back soon to start a new job.`}
            </div>
            {error ? (
              <div className="position-absolute text-danger three-quarter-text px-2 error-container">
                {error}
              </div>
            ) : null}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-outline-secondary py-0 px-3 mr-3"
          onClick={toggleModal}
        >
          {requestSuccessful ? 'Close' : 'Cancel'}
        </button>
        <button
          className={`${
            requestSuccessful && 'd-none'
          } btn modal-button-submit py-0 px-4 ${ASSET_SIGNAL_CSV}`}
          onClick={handleSave}
          disabled={!startDate || !endDate}
        >
          {requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Start Job'
          )}
        </button>
      </ModalFooter>
      {popperStyles}
      <style jsx>{`
        .btn {
          font-size: 0.75rem;
        }
        .remove-condition {
          color: var(--icon-link-gray) !important;
        }

        .remove-condition:hover {
          color: var(--text-light-gray) !important;
        }

        .calendar-class {
          z-index: 5000;
        }

        .error-container {
          top: 0.25rem;
          max-height: 2.25rem;
          overflow: scroll;
        }
      `}</style>
    </Modal>
  );
}

ExportSignalCollectionModalDisplay.propTypes = {
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  handleChangeStart: PropTypes.func,
  handleChangeEnd: PropTypes.func,
  handleChangeCombine: PropTypes.func,
  handleSave: PropTypes.func,
  maxDatapointsState: PropTypes.number,
  handleChangeMaxDatapoints: PropTypes.func,
  globalMaxDatapoints: PropTypes.number,
  showTooManyJobsError: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  requestPending: PropTypes.bool,
  requestSuccessful: PropTypes.bool,
  error: PropTypes.string,
  widget: PropTypes.object,
  width: PropTypes.number,
};
